/**
 * MCA21SourceLookup — official Sandbox MCA Master Data registry lookup.
 *
 * Distinct from the AI-driven cross-source intelligence panel on MCA21Page —
 * this component talks DIRECTLY to the government-mirrored Sandbox.co.in
 * MCA21 API and renders every field the registry returns, no AI synthesis.
 *
 * Contract with the backend (POST /api/mca21/source-lookup):
 *   request:  { cin: string }
 *   200:     { result, source: "MCA21_SANDBOX"|"MCA21_MOCK", mock, looked_up_at, cin }
 *   400-503: { detail: { code, message, source, looked_up_at } }
 *
 * UX guarantees:
 *   - Errors render INLINE (banner) — never via toast or full-page redirect.
 *   - We do not let the global axios 401 path kick the user to /login,
 *     because the backend never returns 401 here (auth failures upstream
 *     are mapped to 502/503 with stable `code` fields).
 *   - Empty fields from the registry show "Not available", not blank.
 *   - Data source + ISO timestamp are stamped under every result for audit.
 *   - When MCA_MOCK_MODE=true on the backend, a "DEMO MODE" badge is shown.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Building2, Search, Loader2, AlertOctagon, Users, Receipt, Clock,
  Hash, MapPin, Mail, Banknote, FileText, Calendar, Flag, FlaskConical,
} from "lucide-react";

const API = `${window.location.origin}/api/mca21`;

const STATUS_COLORS = {
  "Active":             "#22c55e",
  "Strike Off":         "#ef4444",
  "Under Liquidation":  "#dc2626",
  "Dormant":            "#f59e0b",
  "Amalgamated":        "#94a3b8",
};

function Field({ icon: Icon, label, value, mono }) {
  const display = value || "Not available";
  const isMissing = display === "Not available";
  return (
    <div data-testid={`mca-field-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{ background: "#080c12", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        {Icon && <Icon style={{ width: 11, height: 11, color: "#64748b" }} />}
        <span style={{ color: "#64748b", fontSize: 10, fontWeight: 700,
                       textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</span>
      </div>
      <div style={{
        color: isMissing ? "#475569" : "#e2e8f0",
        fontSize: 13, fontWeight: 600,
        fontFamily: mono ? "ui-monospace, monospace" : "inherit",
        fontStyle: isMissing ? "italic" : "normal",
      }}>{display}</div>
    </div>
  );
}

function formatINR(v) {
  if (!v || v === "Not available") return "Not available";
  const n = Number(String(v).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n)) return String(v);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function MCA21SourceLookup() {
  const [cin, setCin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);    // { code, message }
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/source-lookup/config`,
        { withCredentials: true });
      setConfig(data);
    } catch { /* config endpoint is advisory; ignore failures */ }
  }, []);

  useEffect(() => { void loadConfig(); }, [loadConfig]);

  async function runLookup() {
    setError(null);
    setData(null);
    const trimmed = cin.trim().toUpperCase();
    if (trimmed.length !== 21) {
      setError({ code: "CLIENT_INVALID", message: "CIN must be 21 characters (e.g. U72900KA2020PTC123456)." });
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/source-lookup`,
        { cin: trimmed },
        {
          withCredentials: true,
          timeout: 20_000,                                    // a hair over backend's 15s budget
          validateStatus: (s) => s >= 200 && s < 600,         // never throw → no axios 401-interceptor redirect
        });
      if (data && data.result) {
        setData(data);
      } else if (data && data.detail) {
        setError({
          code: data.detail.code || "MCA_SOURCE_UNAVAILABLE",
          message: data.detail.message || "MCA source unavailable, please retry.",
        });
      } else {
        setError({ code: "MCA_SOURCE_UNAVAILABLE", message: "MCA source unavailable, please retry." });
      }
    } catch (e) {
      // Network-level failure (timeout / DNS / abort). Session stays alive.
      setError({
        code: e.code === "ECONNABORTED" ? "MCA_CLIENT_TIMEOUT" : "MCA_NETWORK_ERROR",
        message: "MCA source unavailable, please retry.",
      });
    } finally {
      setBusy(false);
    }
  }

  const r = data?.result;
  const statusColor = r ? (STATUS_COLORS[r.status] || "#94a3b8") : "#94a3b8";

  return (
    <div data-testid="mca-source-lookup" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20, marginBottom: 18 }}>
      <div className="flex items-center" style={{ gap: 10, marginBottom: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Building2 style={{ width: 18, height: 18, color: "#60a5fa" }} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, margin: 0 }}>
            Official MCA Registry Lookup <span style={{ color: "#60a5fa" }}>· Sandbox source</span>
          </h2>
          <div style={{ color: "#94a3b8", fontSize: 11, margin: "2px 0 0 0" }}>
            Direct call to <code style={{ color: "#cbd5e1" }}>api.sandbox.co.in/mca/company/master-data/search</code>. Every field below is what the government registry returned — no AI synthesis.
          </div>
        </div>
        {config?.mock_mode && (
          <div data-testid="mca-demo-badge" style={{ background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.45)", color: "#c4b5fd", padding: "4px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}>
            <FlaskConical style={{ width: 11, height: 11 }} /> DEMO MODE
          </div>
        )}
        {config && !config.mock_mode && !config.credentials_configured && (
          <div data-testid="mca-creds-warn" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.45)", color: "#fbbf24", padding: "4px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
            CREDENTIALS MISSING
          </div>
        )}
      </div>

      {/* Lookup row */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input data-testid="mca-cin-input"
          value={cin}
          onChange={(e) => setCin(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !busy) runLookup(); }}
          placeholder="Enter 21-char CIN (e.g. U72900KA2020PTC123456)"
          style={{ flex: 1, background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "11px 14px", color: "#f1f5f9", fontSize: 13, outline: "none", fontFamily: "ui-monospace, monospace", textTransform: "uppercase" }} />
        <button data-testid="mca-lookup-btn"
          onClick={runLookup} disabled={busy || cin.trim().length === 0}
          style={{ background: busy ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "white", border: "none", borderRadius: 8, padding: "11px 22px", fontSize: 13, fontWeight: 700, cursor: busy ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          {busy ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Search style={{ width: 13, height: 13 }} />}
          {busy ? "Looking up…" : "Lookup"}
        </button>
      </div>

      {/* Inline error banner — NEVER toast, NEVER redirect */}
      {error && (
        <div data-testid="mca-error-banner" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.40)", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 10 }}>
          <AlertOctagon style={{ width: 16, height: 16, color: "#f87171", flexShrink: 0 }} />
          <div>
            <div style={{ color: "#fca5a5", fontSize: 12, fontWeight: 700 }}>
              MCA source unavailable, please retry.
            </div>
            <div style={{ color: "#fecaca", fontSize: 11, marginTop: 2 }}>
              <code style={{ color: "#fed7aa" }}>{error.code}</code> · {error.message}
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {r && (
        <div data-testid="mca-result">
          {/* Identity strip */}
          <div style={{ background: "#080c12", borderRadius: 10, padding: 14, marginBottom: 12, border: "1px solid #1e2530" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Company</div>
                <h3 data-testid="mca-company-name" style={{ color: "#f1f5f9", fontSize: 20, fontWeight: 700, margin: "2px 0 0 0" }}>{r.company_name}</h3>
                <div style={{ color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace", marginTop: 4 }}>{r.cin}</div>
              </div>
              <div data-testid="mca-status-badge" style={{ background: `${statusColor}22`, border: `1px solid ${statusColor}`, color: statusColor, padding: "6px 14px", borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {r.status}
              </div>
            </div>
          </div>

          {/* All identity + financial fields */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
            <Field icon={Calendar} label="Registration Date"    value={r.registration_date} />
            <Field icon={Flag}     label="Company Class"        value={r.company_class} />
            <Field icon={Flag}     label="Company Category"     value={r.company_category} />
            <Field icon={Flag}     label="Company Sub-category" value={r.company_subcategory} />
            <Field icon={Banknote} label="Authorized Capital"   value={formatINR(r.authorized_capital)} />
            <Field icon={Banknote} label="Paid-up Capital"      value={formatINR(r.paid_up_capital)} />
            <Field icon={Hash}     label="ROC"                  value={r.roc} />
            <Field icon={Calendar} label="Last AGM"             value={r.last_agm_date} />
            <Field icon={Calendar} label="Last Balance Sheet"   value={r.last_balance_sheet} />
          </div>

          {/* Address + email + activity */}
          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 8, marginBottom: 12 }}>
            <Field icon={MapPin}  label="Registered Office"        value={r.registered_office} />
            <Field icon={Mail}    label="Email on Record"          value={r.email} />
            <div style={{ gridColumn: "1 / span 2" }}>
              <Field icon={FileText} label="Principal Business Activity" value={r.principal_activity} />
            </div>
          </div>

          {/* Directors */}
          <div data-testid="mca-directors-card" style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <div className="flex items-center" style={{ gap: 8, marginBottom: 10 }}>
              <Users style={{ width: 14, height: 14, color: "#60a5fa" }} />
              <h4 style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, margin: 0 }}>
                Directors & Signatories <span style={{ color: "#64748b", fontWeight: 500 }}>· {r.directors_count}</span>
              </h4>
            </div>
            {r.directors.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 12, fontStyle: "italic" }}>Not available</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 8, fontSize: 12 }}>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Name</div>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>DIN</div>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Designation</div>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Appointment</div>
                {r.directors.map((d) => (
                  <React.Fragment key={`${d.din}-${d.name}`}>
                    <div style={{ color: "#e2e8f0", fontWeight: 600 }}>{d.name}</div>
                    <div style={{ color: "#cbd5e1", fontFamily: "ui-monospace, monospace" }}>{d.din}</div>
                    <div style={{ color: "#94a3b8" }}>{d.designation}</div>
                    <div style={{ color: "#94a3b8" }}>{d.appointment_date}</div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Charges */}
          <div data-testid="mca-charges-card" style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <div className="flex items-center" style={{ gap: 8, marginBottom: 10 }}>
              <Receipt style={{ width: 14, height: 14, color: "#fbbf24" }} />
              <h4 style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, margin: 0 }}>
                Index of Charges <span style={{ color: "#64748b", fontWeight: 500 }}>· {r.charges_count}</span>
              </h4>
            </div>
            {r.charges.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 12, fontStyle: "italic" }}>Not available</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr 1fr 0.8fr", gap: 8, fontSize: 12 }}>
                {["Charge ID", "Holder", "Amount", "Date Created", "Status"].map((h) => (
                  <div key={h} style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</div>
                ))}
                {r.charges.map((c) => (
                  <React.Fragment key={c.charge_id}>
                    <div style={{ color: "#cbd5e1", fontFamily: "ui-monospace, monospace" }}>{c.charge_id}</div>
                    <div style={{ color: "#e2e8f0", fontWeight: 600 }}>{c.holder}</div>
                    <div style={{ color: "#fbbf24" }}>{formatINR(c.amount)}</div>
                    <div style={{ color: "#94a3b8" }}>{c.date_created}</div>
                    <div style={{ color: c.status === "Open" ? "#f87171" : "#94a3b8" }}>{c.status}</div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Audit-trace footer — source + timestamp */}
          <div data-testid="mca-audit-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#080c12", borderRadius: 8, border: "1px solid #1e2530", fontSize: 11 }}>
            <div style={{ color: "#94a3b8", display: "flex", alignItems: "center", gap: 6 }}>
              <Hash style={{ width: 11, height: 11 }} />
              Data source <strong style={{ color: data.mock ? "#c4b5fd" : "#60a5fa" }}>{data.source}</strong>
              {data.mock && <span style={{ color: "#c4b5fd", fontSize: 10, fontWeight: 700, marginLeft: 4 }}>(DEMO MODE)</span>}
            </div>
            <div style={{ color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
              <Clock style={{ width: 11, height: 11 }} />
              Looked up at <span style={{ color: "#cbd5e1", fontFamily: "ui-monospace, monospace" }}>{new Date(data.looked_up_at).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
